import * as React from 'react';
import { connect } from 'react-redux';
import isEmpty from 'lodash/isEmpty';

import ActionButton from 'components/buttons/action-button/ActionButton';

import InsightsReportsGraphOverTime from 'modules/insights/reports/components/graphs/InsightsReportsGraphOverTime';
import InsightsReportsAnalysisSummary from '../summary/InsightsReportsAnalysisSummary';
import InsightsReportsAnalysisResultsSection from './section/InsightsReportsAnalysisResultsSection';
import InsightsReportsAnalysisPosts from 'modules/insights/reports/components/analysis/posts/InsightsReportsAnalysisPosts';

import { runAnalysis } from 'modules/insights/reports/reducers/analysis/collabary/insights-reports-analysis';
import {
  fetchPosts,
  postsUpdateFiltersScreenState,
  postsUpdateScreenState
} from 'modules/insights/reports/reducers/posts/collabary/insights-reports-posts';

import { __t, applyTranslations } from 'utils/language';
import { scrollToMissingField } from 'utils/page';
import { getGraphDateInterval } from 'utils/graphs/utils';

import './insights-reports-analysis-results.scss';

import type { LineGraphEChartsObject, LocationWindow } from 'types';
import type {
  Analysis as AnalysisResult,
  Query,
  Report,
  ReportType
} from 'modules/insights/reports/types';

applyTranslations(require('modules/insights/reports/shared/insights-reports-shared.i18n.json'));
applyTranslations(require('./insights-reports-analysis-results.i18n.json'));

type OwnProps = {
  reportType: ReportType,
  isValidSetup: boolean,
  location: LocationWindow,
  chartInstanceCallback?: (echartsInstance: LineGraphEChartsObject) => void,
  onFormStatusCheck: () => void
};

type StateProps = {
  report: Report,
  analysis: AnalysisResult,
  queries: Array<Query>,
  analysisHasBeenRun: boolean,
  isLoadingAnalysis: boolean
};

type DispatchProps = {
  dispatchRunAnalysis: (report: Report) => void,
  dispatchFetchPosts: (report: Report, reportType: ReportType) => void,
  dispatchPostsUpdateScreenState: (value: any, propName: string) => void,
  dispatchUpdateFiltersScreenState: (value: any, propName: string) => void
};

type Props = OwnProps & StateProps & DispatchProps;

const SHARED_MSG_KEY = 'insights_reports_shared';
const MSG_KEY = 'insights_reports_analysis_results';

class InsightsReportsAnalysisResults extends React.PureComponent<Props> {
  containerRef: React.RefObject<HTMLDivElement> = React.createRef();

  handleFetchPosts = (): void => {
    const { report, reportType, dispatchFetchPosts } = this.props;

    dispatchFetchPosts(report, reportType);
  };

  handleAnalysis = (): void => {
    const {
      report,
      reportType,
      isValidSetup,
      onFormStatusCheck,
      dispatchRunAnalysis,
      dispatchFetchPosts,
      dispatchPostsUpdateScreenState,
      dispatchUpdateFiltersScreenState
    } = this.props;

    onFormStatusCheck();
    if (!isValidSetup) {
      scrollToMissingField(true);
      return;
    }

    const containerRef = this.containerRef && this.containerRef.current;

    if (containerRef) {
      containerRef.scrollIntoView({ behavior: 'smooth' });
    }

    dispatchRunAnalysis(report);
    dispatchUpdateFiltersScreenState([], 'queryIds');
    dispatchPostsUpdateScreenState('', 'channel');
    dispatchPostsUpdateScreenState(1, 'page');
    dispatchFetchPosts(report, reportType);
  };

  renderGraph(): React.ReactNode {
    const { analysis, isLoadingAnalysis, report, reportType, chartInstanceCallback } = this.props;
    const reportName = (report && report.name) || __t(`${MSG_KEY}.labels.unnamed_report`);
    const dateInterval = getGraphDateInterval(report.startDate, report.endDate);
    const graphKey = (analysis.graphOverTime || []).map(item => item.searchTerm).join(',');

    return (
      <InsightsReportsAnalysisResultsSection
        isEmpty={isEmpty(analysis) || isEmpty(analysis.graphOverTime)}
        isLoading={isLoadingAnalysis}
        header={__t(`${MSG_KEY}.labels.report`)}
        subline={__t(`${SHARED_MSG_KEY}.messages.included_results`)}
      >
        <InsightsReportsGraphOverTime
          key={`insights-reports-analysis-results__graph--${graphKey}`}
          chartInstanceCallback={chartInstanceCallback}
          results={analysis.graphOverTime}
          reportName={reportName}
          reportType={reportType}
          dateInterval={dateInterval}
          totalUniqueInfluencers={analysis.totalUniqueContentCreators}
        />
      </InsightsReportsAnalysisResultsSection>
    );
  }

  renderSummary(): React.ReactNode {
    const { analysis, reportType, isLoadingAnalysis } = this.props;
    const summaryTable = analysis.summaryTable || {};
    return (
      <InsightsReportsAnalysisResultsSection
        isEmpty={isEmpty(analysis) || isEmpty(analysis.summaryTable)}
        isLoading={isLoadingAnalysis}
        header={__t(`${MSG_KEY}.labels.analysis_summary`)}
      >
        <InsightsReportsAnalysisSummary
          summaryTable={summaryTable}
          reportType={reportType}
        />
      </InsightsReportsAnalysisResultsSection>
    );
  }

  renderPosts(): React.ReactNode {
    const { reportType, queries, location, isLoadingAnalysis } = this.props;

    return (
      <InsightsReportsAnalysisPosts
        key={String(isLoadingAnalysis)}
        queries={queries}
        reportType={reportType}
        location={location}
        onFetchPosts={this.handleFetchPosts}
      />
    );
  }

  renderAnalysisButton(): React.ReactNode {
    return (
      <div className="insights-reports-analysis-results__button">
        <ActionButton
          color="primary"
          width="maximum"
          onClick={this.handleAnalysis}
        >
          {__t(`${MSG_KEY}.buttons.run_analysis`)}
        </ActionButton>
      </div>
    );
  }

  render(): React.ReactNode {
    const { analysisHasBeenRun } = this.props;

    return (
      <div className="insights-reports-analysis-results">
        {this.renderAnalysisButton()}
        <div
          ref={this.containerRef}
          className="insights-reports-analysis-results__container"
        >
          {
            analysisHasBeenRun && (
              <React.Fragment>
                {this.renderGraph()}
                {this.renderSummary()}
                {this.renderPosts()}
              </React.Fragment>
            )
          }
        </div>
      </div>
    );
  }
}

const mapStateToProps = ({
  insightsReports,
  insightsReportsAnalysis,
  insightsReportsQueries
}): StateProps => ({
  report: insightsReports.report,
  analysis: insightsReportsAnalysis.analysis,
  queries: insightsReportsQueries.queries,
  isLoadingAnalysis: insightsReportsAnalysis.isLoading,
  analysisHasBeenRun: insightsReportsAnalysis.analysisHasBeenRun
});

const mapDispatchToProps = (dispatch): DispatchProps => ({
  dispatchRunAnalysis: (report: Report) => (
    dispatch(runAnalysis(report))
  ),
  dispatchFetchPosts: (report: Report, reportType: ReportType) => (
    dispatch(fetchPosts(report, reportType))
  ),
  dispatchPostsUpdateScreenState: (value: any, propName: string) => (
    dispatch(postsUpdateScreenState(value, propName))
  ),
  dispatchUpdateFiltersScreenState: (value: any, propName: string) => (
    dispatch(postsUpdateFiltersScreenState(value, propName))
  )
});

export default connect(mapStateToProps, mapDispatchToProps)(InsightsReportsAnalysisResults);
